/**
 * Entity Configuration for DuplicateEntity Component
 *
 * This file contains configurations for different entity types that can be duplicated,
 * including their mutations, refetch queries, and additional fields.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import { GET_PROPERTIES } from '@/housefolios-components/Properties/queries'
import { GET_PORTFOLIOS, GET_SIDEBAR_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_COMMERCIAL_PROPERTIES } from '@/housefolios-components/Lots/queries'
import { GET_LOTS } from '@/housefolios-components/Lots/queries'
import { GET_INVESTORS_BY_PORTFOLIO } from '@/housefolios-components/Investor/queries'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'

// Property duplication configuration
export const PROPERTY_CONFIG = {
  entityType: 'property',
  mutation: 'COPY_PROPERTY', // This mutation exists in Property/mutations.js
  refetchQueries: [
    {
      query: GET_PROPERTIES,
      variables: {
        portfolioId: ['SELECTED_PORTFOLIO'],
        limit: GET_PROPERTIES_QUERY_LIMIT,
      },
    },
    {
      query: GET_PROPERTIES,
      variables: {
        portfolioId: ['ALL_PORTFOLIOS'],
        limit: GET_PROPERTIES_QUERY_LIMIT,
      },
    },
    { query: GET_PORTFOLIOS },
    { query: GET_SIDEBAR_PORTFOLIOS },
    { query: GET_ME },
  ],
  additionalFields: {},
  successMessage: 'Property was duplicated successfully',
  errorMessage: 'Property failed to duplicate',
}

// Portfolio duplication configuration
export const PORTFOLIO_CONFIG = {
  entityType: 'portfolio',
  mutation: 'COPY_PORTFOLIO', // This mutation exists in Portfolio/mutations.js
  refetchQueries: [
    { query: GET_PORTFOLIOS },
    { query: GET_SIDEBAR_PORTFOLIOS },
    { query: GET_ME },
  ],
  additionalFields: {
    name: {
      type: 'text',
      label: 'Portfolio Name',
      required: true,
    },
    description: {
      type: 'text',
      label: 'Description',
    },
    strategy: {
      type: 'select',
      label: 'Strategy',
      options: [
        { value: 'buyHold', label: 'Buy & Hold' },
        { value: 'fixFlip', label: 'Fix & Flip' },
        { value: 'brrrr', label: 'BRRRR' },
        { value: 'turnkey', label: 'Turnkey' },
        { value: 'shortTermRental', label: 'Short Term Rental' },
        { value: 'wholesale', label: 'Wholesale' },
        { value: 'buildToRent', label: 'Build to Rent' },
      ],
    },
    copyAssumptions: {
      type: 'checkbox',
      label: 'Copy Assumptions',
    },
  },
  successMessage: 'Portfolio was duplicated successfully',
  errorMessage: 'Portfolio failed to duplicate',
}

// Commercial property duplication configuration
export const COMMERCIAL_CONFIG = {
  entityType: 'commercial',
  mutation: 'COPY_COMMERCIAL', // This mutation exists in Commercial/DuplicateCommercial/mutations.js
  refetchQueries: [
    {
      query: GET_COMMERCIAL_PROPERTIES,
      variables: { portfolioId: ['SELECTED_PORTFOLIO'] },
    },
    { query: GET_PORTFOLIOS },
    { query: GET_SIDEBAR_PORTFOLIOS },
    { query: GET_ME },
  ],
  additionalFields: {},
  successMessage: 'Commercial property was duplicated successfully',
  errorMessage: 'Commercial property failed to duplicate',
}

// Lot duplication configuration
export const LOT_CONFIG = {
  entityType: 'lot',
  mutation: 'COPY_LOT', // This mutation exists in Lot/DuplicateLot/mutations.js
  refetchQueries: [
    {
      query: GET_LOTS,
      variables: { portfolioId: ['SELECTED_PORTFOLIO'] },
    },
    { query: GET_PORTFOLIOS },
    { query: GET_SIDEBAR_PORTFOLIOS },
    { query: GET_ME },
  ],
  additionalFields: {},
  successMessage: 'Lot was duplicated successfully',
  errorMessage: 'Lot failed to duplicate',
}

// Investor duplication configuration
export const INVESTOR_CONFIG = {
  entityType: 'investor',
  mutation: 'COPY_INVESTOR', // This mutation exists in Investor/mutations.js
  refetchQueries: [
    {
      query: GET_INVESTORS_BY_PORTFOLIO,
      variables: { portfolioId: 'SELECTED_PORTFOLIO' },
    },
    { query: GET_PORTFOLIOS },
    { query: GET_SIDEBAR_PORTFOLIOS },
    { query: GET_ME },
  ],
  additionalFields: {},
  successMessage: 'Investor was duplicated successfully',
  errorMessage: 'Investor failed to duplicate',
}

// Multiple properties duplication configuration
export const MULTIPLE_PROPERTIES_CONFIG = {
  entityType: 'properties',
  mutation: 'COPY_PROPERTIES', // This mutation exists in Property/mutations.js
  refetchQueries: [
    {
      query: GET_PROPERTIES,
      variables: {
        portfolioId: ['SELECTED_PORTFOLIO'],
        limit: GET_PROPERTIES_QUERY_LIMIT,
      },
    },
    {
      query: GET_PROPERTIES,
      variables: {
        portfolioId: ['ALL_PORTFOLIOS'],
        limit: GET_PROPERTIES_QUERY_LIMIT,
      },
    },
    { query: GET_PORTFOLIOS },
    { query: GET_SIDEBAR_PORTFOLIOS },
    { query: GET_ME },
  ],
  additionalFields: {},
  successMessage: 'Properties were duplicated successfully',
  errorMessage: 'Properties failed to duplicate',
}

// Configuration mapping
export const ENTITY_CONFIGS = {
  property: PROPERTY_CONFIG,
  portfolio: PORTFOLIO_CONFIG,
  commercial: COMMERCIAL_CONFIG,
  lot: LOT_CONFIG,
  investor: INVESTOR_CONFIG,
  properties: MULTIPLE_PROPERTIES_CONFIG,
}

// Helper function to get configuration by entity type
export const getEntityConfig = (entityType) => {
  return ENTITY_CONFIGS[entityType] || PROPERTY_CONFIG
}

// Helper function to process refetch queries with dynamic portfolio IDs
export const processRefetchQueries = (
  config,
  selectedPortfolio,
  allPortfolios,
) => {
  return config.refetchQueries.map((query) => {
    if (query.variables) {
      const processedVariables = { ...query.variables }

      // Replace placeholder portfolio IDs
      if (processedVariables.portfolioId) {
        if (Array.isArray(processedVariables.portfolioId)) {
          processedVariables.portfolioId =
            processedVariables.portfolioId
              .map((id) => {
                if (id === 'SELECTED_PORTFOLIO')
                  return selectedPortfolio
                if (id === 'ALL_PORTFOLIOS')
                  return allPortfolios.map((p) => p._id)
                return id
              })
              .flat()
        } else if (
          processedVariables.portfolioId === 'SELECTED_PORTFOLIO'
        ) {
          processedVariables.portfolioId = selectedPortfolio
        }
      }

      return { ...query, variables: processedVariables }
    }
    return query
  })
}
